import ErrorLine from 'components/ErrorLine'
import Hls from "hls.js"
import { store } from 'index'
import { useState } from 'react'
import { useImperativeHandle } from 'react'
import { forwardRef } from 'react'
import { useEffect, useRef, memo } from "react"
import { isIOS } from 'react-device-detect'
import { useSelector } from 'react-redux'
import { setVideoPlaying } from 'redux/actions/playerControlActions'
import { setMediaDuration, setPreview } from 'redux/actions/playerControlActions'
import { getMediaAudio } from 'util/imageUtils'
import { setCurrentPlayTime } from 'redux/actions/reactorActions'
import { checkCreditUsage } from 'util/creditUtils'
import { hasNext } from 'util/playlistUtils'
import { getAudioSrc } from 'util/playlistUtils'

import { getVideoSrc } from 'util/playlistUtils'
import { displayError } from 'util/screenUtils'
import { next, previous } from 'util/playlistUtils'
import MediaHtmlComponent from './MediaHtmlComponent'
import VideoPlayerControls from './VideoPlayerControls'
import { isMediaPending } from 'util/postUtils'
import MediaPending from './MediaPending'
import DisplayHarmonizePost from 'vortex/DisplayHarmonizePost'
import { getCurrentPlaylistEntry } from 'util/playlistUtils'
import { log } from 'components/utility/DisplayLog'
import { isSupport } from 'util/postUtils'
import { trackCurrentPlaylistEntry } from 'util/analyticsUtil'
import { ANALYTICS_EVENTS } from 'util/analyticsUtil'
import { PLAYER_MODES } from 'redux/reducers/harmonizeReducer'
import { isSongPreview } from 'util/postUtils'

/** 
 * The video will start at startPosition.
 * 
 * If endPosition is provided, it will loop between startPosition and endPosition until the
 * player fullscreen or volume buttons are pressed. These interactions disable looping so
 * that the user can view the entire video.
 * 
 * Use hls.js for HLS because video.js doesn't work for HLS as of 11/20/2024.
 * 
 * *** NOTE *** The biggest challenge to having this work on iOS was the handling of
 * the muted/autoplay parameters.
 * 1) We don't need autoplay so it was discarded
 * 2) The <video> element *must* have the muted parameter or the video cannot be
 * started with video.play().
 * 
 * Once the user taps the mute button (to unmute the sound) it stays unmuted
 * but only for multiple videos in a row -- an <audio> element resets it..
 * 
 * @see https://github.com/video-dev/hls.js
 * @see https://stackoverflow.com/questions/76632259/how-to-use-hls-js-with-react-properly
 */

let interval
export const MEDIA_JUMP_LENGTH = 5
const MediaPlayer = forwardRef(function (props, controlRef) {

    const { noPlayerControls,
        videoStyle,
        source, dispatch,
        controls = false,
        setPosterImage,
        autoHeight = false,
        videoOnly = false,
        click } = props
    const [outOfCredits, setOutOfCredits] = useState(false)
    const { playListIndex } = useSelector(state => state.harmonize)
    const { playList } = useSelector(state => state.messages)
    const { mediaDuration } = useSelector(state => state.playerControls)
    const [error, setError] = useState()
    const mediaRef = useRef()
    const mediaPlayingRef = useRef(false)
    const delayedSwipeRef = useRef(0)
    const totalPlayTimeRef = useRef(0)

    useImperativeHandle(controlRef, // forwarded ref
        function () {
            return {
                fullscreen() {
                    requestFullscreen()
                },
                nextMedia() {
                    handleSwipe(true)
                },
                previousMedia() {
                    handleSwipe(false)
                },
                play() {
                    log('<>MediaPlayer ref play')
                    playVideo()
                },
                pause() {
                    log('MediaPlayer ref pause')
                    if (mediaRef.current) {
                        mediaRef.current.pause()
                    }
                },
                seekTo(time) {
                    log(`<>MediaPlayer ref seekTo ${time}`)
                    mediaRef.current.currentTime = time
                }
            } // the forwarded ref value
        }, [])


    const checkCredits = async () => {
        if (!outOfCredits && getSong()) {
            try {
                await checkCreditUsage(getSong(),
                    dispatch)
            } catch (error) {
                console.error('----------- OUT OF CREDITS')
                setOutOfCredits(true)
                dispatch(setPreview(true))
            }
        }
    }
    const drawFrame = () => {
        if (setPosterImage) {
            try {
                const video = mediaRef.current
                log(`MediaPlayer drawFrame ${video.videoWidth}x${video.videoHeight}`)
                const canvas = window.document.createElement('canvas')
                const ctx = canvas.getContext('2d')
                canvas.width = video.videoWidth
                canvas.height = video.videoHeight
                video.pause()
                ctx.drawImage(video, 0, 0,)
                canvas.toBlob((blob) => {
                    const { name } = getSong()
                    const nameOnly = name.split('.')[0]
                    const posterFile = new File([blob], `${nameOnly}-poster.jpg`, {
                        type: 'image/jpeg',
                    })
                    setPosterImage(posterFile)
                }, 'image/jpeg')
            } catch (error) {
                console.error('MediaPlayer drawFrame', error)
            }
        }
    }
    const initDrawFrame = () => {
        const video = mediaRef.current
        log(`MediaPlayer loadedmetadata videoWidth ${video.videoWidth} videoHeight ${video.videoHeight}`)
        video.muted = true
        video.play()
    }

    const isVideo = () => {
        return source || getVideoSrc(getSong())
    }
    /**
     * If source is provided, use this. This supports using <video> with 
     * selected files in e.g. CreateHarmonizePost.
     * 
     * Otherwise if there is a video source in the current playList entry, return it.
     * 
     * Otherwise return the audio source from the current playList entry.
     * @returns 
     */
    const getActualMediaSrc = () => {
        if (source) {
            return source
        } else {
            const videoSrc = getVideoSrc(getSong())
            if (videoSrc) {
                return videoSrc
            } else {
                return getAudioSrc(getSong())
            }
        }
    }
    const getSong = () => {
        return playList[playListIndex]
    }
    const hasUserStartedPlayback = () => {
        const { userStartedPlayback } = store.getState().playerControls
        return userStartedPlayback
    }
    const isContinuousPlay = () => {
        const { continuousPlay } = store.getState().display
        return continuousPlay
    }

    const isPreview = () => {
        const { preview } = store.getState().playerControls
        return preview
    }
    const shouldMute = () => {
        const { userStartedPlayback, userMutedMedia } = store.getState().playerControls
        return (isIOS && !userStartedPlayback) || userMutedMedia
    }

    const shouldLoop = () => {
        const doLoop = mediaRef.current && mediaRef.current.duration < 30
            && getVideoSrc(getSong())
            && !isContinuousPlay()
        //console.log(`shouldLoop ${doLoop}`)
        return doLoop
    }
    /**
     * Don't start playing if not in SWIPE playerMode or you may have
     * multiple streams playing simultaneously on the samge page.
     * @returns 
     */
    const shouldPlayNow = () => {
        const { playerMode } = store.getState().harmonize
        const { userStartedPlayback, userPausedMedia } = store.getState().playerControls
        const doPlayNow = playerMode === PLAYER_MODES.SWIPE
            && !userPausedMedia
            && (getVideoSrc(getSong()) || userStartedPlayback)
        console.log(`shouldPlayNow ${doPlayNow} playerMode ${playerMode} userStartedPlayback ${userStartedPlayback} userPausedMedia ${userPausedMedia}`)
        return doPlayNow
    }
    const getPositions = () => {
        const { highlightTime } = getSong() ? getSong() : { highlightTime: 0 }
        const startPosition = isPreview() && highlightTime ? highlightTime : 0
        const endPosition = isPreview() ? startPosition + parseInt(process.env.REACT_APP_AUDIO_PREVIEW_LENGTH) : 0
        //console.log(`<>MediaPlayer highlightTime ${highlightTime} start ${startPosition} end ${endPosition}`)
        return { startPosition, endPosition }
    }

    //log(`MediaPlayer HLS set video currentTime to ${getPositions().startPosition}`)
    const hls = new Hls({
        "debug": false,
        startPosition: getPositions().startPosition //This is currently the only way to get the correct start position set
    })
    /** The duration is only known after the first Level from HLS is loaded  */
    hls.on(Hls.Events.LEVEL_LOADED, function (event, data) {
        //log(`<>MediaPlayer level loaded`, data.details)
        //log(`<>MediaPlayer level loaded Video duration: ${mediaElement.duration}`)
        dispatch(setMediaDuration(mediaRef.current.duration))
    })

    /**
     * The currentPlayTime is the absolute 0-based time. This is used in the Reactor to
     * create reactions. The startPosition, if any, must be added to the video currentTime
     * to get the 0-based time.
     * @returns Rounded video currentTime
     */
    const getElapsedTime = () => {
        const mediaElement = mediaRef.current
        let elapsedTime = 0
        if (mediaElement) {
            elapsedTime = Math.round(mediaElement.currentTime)
            //log((`<>MediaPlayer elapsedTime ${elapsedTime}`))
            //const actualTime = mediaElement.currentTime + startPos
            //log((`<>MediaPlayer startPosition ${startPosition} elapsedTime ${elapsedTime} actualTime ${actualTime}`))
            dispatch(setCurrentPlayTime(elapsedTime))
        }
        return elapsedTime
    }

    const trackAndReset = () => {
        trackCurrentPlaylistEntry(ANALYTICS_EVENTS.STOP_PLAYBACK, totalPlayTimeRef.current)
        totalPlayTimeRef.current = 0
    }
    /**
     * By observation on real devices allowing uncontrolled swipe would result in
     * multiple audio sources playing at once. The current solution is to delegate
     * handling swipe to this component. Then, if the current media element is playing,
     * we either call pause or wait. If delayedSwipeRef is 0, we set it to 1 for forward,
     * 2 for previous, then call pause. If it is *not* 0, it means we are still waiting for
     * the pause to complete, and do nothing.
     * 
     * IF the current media element is not playing, we just call next or forward.
     * @param {*} forward 
     */
    const handleSwipe = (forward) => {
        if (isSupport(getSong())) {
            if (forward) {
                next(dispatch, true)
            } else {
                previous(dispatch, true)
            }
        } else {
            const mediaElement = mediaRef.current
            if (mediaElement && !mediaElement.paused) {
                if (delayedSwipeRef.current === 0) {
                    log(`<>MediaPlayer handleSwipe PAUSE`)
                    delayedSwipeRef.current = forward ? 1 : 2
                    mediaElement.pause()
                } else {
                    log(`<>MediaPlayer WAITING ON PAUSE`)
                }
            } else {
                delayedSwipeRef.current = 0
                if (forward) {
                    trackAndReset()
                    next(dispatch, true)
                } else {
                    trackAndReset()
                    previous(dispatch, true)
                }
            }
        }

    }
    const handlePause = () => {
        log(`<>MediaPlayer handlePause delayedSwipeRef ${delayedSwipeRef.current}`)
        clearInterval(interval)
        mediaPlayingRef.current = false
        dispatch(setVideoPlaying(false))
        if (delayedSwipeRef.current !== 0) {
            if (delayedSwipeRef.current === 1) {
                log(`<>MediaPlayer handlePause NEXT`)
                trackAndReset()
                next(dispatch, true)
            } else {
                log(`<>MediaPlayer handlePause PREVIOUS`)
                trackAndReset()
                previous(dispatch, true)
            }
            delayedSwipeRef.current = 0
        }

    }

    const handlePlay = () => {
        const mediaElement = mediaRef.current
        if (mediaElement) {
            const { name } = getSong() ? getSong() : { name: '' }
            const { startPosition, endPosition } = getPositions()
            log(`<>MediaPlayer handlePlay started playing preview ${isPreview()} 
            startPosition ${startPosition}
            endPosition ${endPosition}`)
            mediaPlayingRef.current = true
            dispatch(setVideoPlaying(true))
            //dispatch(setUserStartedPlayback(true))
            totalPlayTimeRef.current = 0
            trackCurrentPlaylistEntry(ANALYTICS_EVENTS.START_PLAYBACK)


            //On an actual iPhone it seems that setting the startPosition in useEffect 
            //does not work
            if (mediaElement.currentTime < startPosition) {
                mediaElement.currentTime = startPosition
            }

            interval = setInterval(() => {
                totalPlayTimeRef.current++
                const elapsedTime = getElapsedTime()
                checkCredits()
                if (endPosition && elapsedTime > endPosition) {
                    //log(`<>MediaPlayer interval check continuousPlay ${isContinuousPlay()}
                    //\nendPosition exceeded elapsedTime ${elapsedTime} endPos ${endPosition}`)
                    if (isContinuousPlay()) {
                        log(`<>MediaPlayer video ${name} interval done, play next`)
                        terminate()
                        next(dispatch)
                    } else {
                        if (!getVideoSrc(getSong())) {
                            terminate()
                        }
                        mediaElement.currentTime = startPosition
                    }
                }
            }, 1000)
        }
    }

    /**

     */
    const terminate = () => {
        log(`<>MediaPlayer TERMINATE media pause mediaRef totalPlayTime ${totalPlayTimeRef.current}`)
        if (mediaRef.current) {
            mediaRef.current.pause()
        }
        if (hls) {
            hls.detachMedia()
        }
        delayedSwipeRef.current = 0
        clearInterval(interval)
        dispatch(setVideoPlaying(false))
        trackAndReset()
    }

    const handleEnded = () => {
        log('<>MediaPlayer media ended')
        if (isContinuousPlay()) {
            clearInterval(interval)
            if (hasNext()) {
                trackAndReset()
                next(dispatch)
            } else {
                terminate()
            }
        }
    }

    /**
     * If the user clicks play and the media is at or past the last playable position,
     * reposition to the start position before startined playback.
     */
    const playVideo = () => {
        const mediaElement = mediaRef.current
        if (mediaElement) {
            const { endPosition, startPosition } = getPositions()
            if ((endPosition === 0 && mediaElement.currentTime >= mediaElement.duration)
                ||
                (endPosition !== 0 && mediaElement.currentTime > endPosition)) {
                mediaElement.currentTime = startPosition
            }
            mediaElement.play().catch(error => console.error('<>MediaPlayer play error', error))
        }
    }

    const setAudioDuration = () => {
        if (getSong()) {
            const { entryMedia } = getSong()
            if (entryMedia) {
                const audioMedia = getMediaAudio(entryMedia)
                const { duration } = audioMedia
                dispatch(setMediaDuration(duration))
            }
        }
    }

    const setupPlayback = () => {
        const mediaElement = mediaRef.current
        //const playNow = shouldLoop() //hasUserStartedPlayback() && (autoplay || isContinuousPlay())
        mediaElement.autoplay = shouldPlayNow()
        log(`<>MediaPlayer setupPlayback autoplay set to ${shouldPlayNow()}`)
        if (isVideo()) {
            log(`<>MediaPlayer setupPlayback play video`)
            hls.loadSource(getActualMediaSrc())
            hls.attachMedia(mediaElement)
        } else {
            setAudioDuration()
            mediaElement.src = getActualMediaSrc()
        }
    }
    useEffect(() => {
        return () => {
            // This code will run when the component unmounts
            log('<>MediaPlayer ----------- UNMOUNTED')
        }
    }, [])

    /** This will fail the Rich Results Test; however it may work with a real crawl */
    const setStructuredData = () => {
        if (getSong()) {
            const { name, message } = getSong()
            const script = document.createElement('script')
            script.type = 'application/ld+json'
            script.innerHTML = `
                {
                "@context": "https://schema.org/",
                "@type": "${getVideoSrc(getSong()) ? 'VideoObject' : 'AudioObject'}",
                "name": "${name}",
                "description": "${message}",
                "contentUrl": "${getActualMediaSrc()}"
                
                }
            `
            document.head.appendChild(script)
            console.log(`<>MediaPlayer added structured data`, script)
            return script
        }
    }
    /** Whenever this is called the media element (currently <video>) is paused, never playing. */
    useEffect(() => {
        const mediaElement = mediaRef.current
        if (outOfCredits) {
            terminate()
        } else if (getActualMediaSrc() && mediaElement) {
            //log(`<>MediaPlayer useEffect playing ref ${mediaPlayingRef.current} `)

            mediaElement.addEventListener('play', handlePlay)
            mediaElement.addEventListener('pause', handlePause)
            mediaElement.addEventListener('ended', handleEnded)
            if (setPosterImage) {
                mediaElement.addEventListener('timeupdate', drawFrame, false)
                mediaElement.addEventListener('loadedmetadata', initDrawFrame, false)
            }
            setupPlayback()
            dispatch(setPreview(isSongPreview(getSong().accessType)))

            /*
            called right before the component unmounts 
            or before the effect runs again due to a change in dependencies.
            */
            return () => {
                //log(`<>MediaPlayer return from useEffect`)
                clearInterval(interval)
                if (hls) {
                    hls.detachMedia()
                }
                mediaElement.removeEventListener('play', handlePlay)
                mediaElement.removeEventListener('pause', handlePause)
                mediaElement.removeEventListener('ended', handleEnded)
                if (setPosterImage) {
                    mediaElement.removeEventListener('timeupdate', drawFrame, false)
                    mediaElement.removeEventListener('loadedmetadata', initDrawFrame, false)
                }
                /*
                if (script) {
                    console.log('<>MediaPlayer CLEAR structured data')
                    document.head.removeChild(script)
                }
                */
            }

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playListIndex, playList])

    const requestFullscreen = () => {
        const element = mediaRef.current
        try {
            if (isIOS) {
                try {
                    element.webkitEnterFullscreen()
                } catch {
                    element.requestFullscreen()
                }
            } else {
                element.requestFullscreen()
            }
        } catch (error) {
            displayError(error, setError)
        }
    }

    const checkRange = (forward) => {
        const { startPosition, endPosition } = getPositions()
        if (forward) {
            if (endPosition === 0 || mediaRef.current.currentTime + MEDIA_JUMP_LENGTH <= endPosition) {
                mediaRef.current.currentTime = mediaRef.current.currentTime + MEDIA_JUMP_LENGTH
            } else {
                if (endPosition) {
                    mediaRef.current.currentTime = endPosition
                } else {
                    mediaRef.current.currentTime = mediaDuration
                }
            }
        } else {
            if (mediaRef.current.currentTime - MEDIA_JUMP_LENGTH >= startPosition) {
                mediaRef.current.currentTime = mediaRef.current.currentTime - MEDIA_JUMP_LENGTH
            } else {
                mediaRef.current.currentTime = startPosition
            }
        }
    }

    const displayPostOrEmpty = () => {
        const entry = getCurrentPlaylistEntry()
        if (entry) {
            return <DisplayHarmonizePost content={entry} fromSwipe />

        } else {
            return null
        }
    }
    const displayNoMedia = () => {
        return (<div style={{ position: 'relative', height: '100vh', backgroundColor: 'lightblue' }}>
            <ErrorLine error={error} />
            {isMediaPending() ? <MediaPending />
                : displayPostOrEmpty()}
            {!noPlayerControls && <VideoPlayerControls
                mediaRef={mediaRef}
                controlRef={controlRef}
                forward={() => checkRange(true)}
                rewind={() => checkRange(false)}

            />}
        </div>)
    }
    const getVideoAndControls = () => {
        return (
            <div style={{ position: 'relative' }}>
                <ErrorLine error={error} />

                <MediaHtmlComponent
                    videoSource={getActualMediaSrc()}
                    ref={mediaRef}
                    loop={shouldLoop()}
                    muted={shouldMute()}
                    autoHeight={autoHeight}
                    videoOnly={videoOnly}
                    click={click}
                    videoStyle={videoStyle} controls={controls} />
                {!noPlayerControls && <VideoPlayerControls
                    mediaRef={mediaRef}
                    controlRef={controlRef}
                    mute={() => mediaRef.current.muted = true}
                    unmute={() => mediaRef.current.muted = false}
                    play={() => playVideo()}
                    pause={() => mediaRef.current.pause()}
                    forward={() => checkRange(true)}
                    rewind={() => checkRange(false)}

                />}
            </div>)
    }
    return Hls.isSupported() ?
        isMediaPending() ? displayNoMedia() : getActualMediaSrc() ? getVideoAndControls() : displayNoMedia()
        : (
            <div>No HLS Support for video</div>
        )
})
export default memo(MediaPlayer)